
import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import ExamV3Custom, {
  ExamV3Tem
} from "@/lib/model/exambuilder/exam-v3/exam-v3";
import { PsyUse } from "@/lib/model/exambuilder/exam-v3/exam-v3-sub";
import CheckTree from "@/views/ai-builder-v3/components/check-tree.vue";
import QuestionCardV3 from "@/views/ai-builder-v3/components/question-card.vue";
import {
  EnumQuestionType,
  QuestionV2,
  QuestionV2Object
} from "@/lib/model/exambuilder/question-v2";
import { Check } from "@/lib/enum/check";
import QuestionDetail from "@/views/ai-builder-v3/components/question-detail.vue";
import QuestionEditV3 from "@/views/ai-builder-v3/components/question-edit-v3.vue";
import {
  Mind,
  MindDimension,
  PsycGaugeConfig
} from "@/lib/model/exambuilder/exam-v2";
import Collapse from "@/views/ai-builder-v3/components/collapse.vue";
import Loading from "@/components/loading.vue";

const psycGaugeIdMap = {
  [Check.BIG_FIVE_240]: 1,
  [Check.DISC]: 3,
  [Check.BIG_FIVE_60]: 4,
  [Check.DVF]: 5
};

@Component({
  components: {
    Loading,
    Collapse,
    QuestionDetail,
    CheckTree,
    QuestionCardV3,
    QuestionEditV3
  }
})
export default class SetQuestionsV3 extends Vue {
  @Prop({ type: String, default: "exam" })
  mode!: "exam" | "template";
  @Prop({ type: String, default: "" })
  tacToken!: string;
  @Prop({ type: Object, default: null })
  exam!: ExamV3Custom | ExamV3Tem;
  @Prop({ default: () => [new PsycGaugeConfig()] })
  mindList!: PsycGaugeConfig[]; // 心理测评list

  get basicQualityList() {
    return this.exam.processInformation.basicQualityList.filter(
      item => item.dimensionKey !== ""
    );
  }

  get physicalInstrument() {
    return this.exam.processInformation.physicalInstrument;
  }

  get knowledgeAndSkills() {
    return this.exam.processInformation.knowledgeAndSkills;
  }

  get psychologicalMotivation() {
    return this.exam.processInformation.psychologicalMotivation;
  }
  get Check() {
    return Check;
  }

  pageLoading = true;

  @Emit()
  status() {
    return !this.pageLoading;
  }

  // 所有心理量表
  chooseObj: PsycGaugeConfig = new PsycGaugeConfig();

  initSelectedPsyc() {
    const { psyc1, psyc4 } = this.psychologicalMotivation;

    if (psyc1.use === 1) {
      const psycId = psycGaugeIdMap[Check.BIG_FIVE_240];
      const mindObj =
        this.mindList.find(item => item.psycGaugeId === psycId) ||
        new PsycGaugeConfig();

      psyc1.psycGaugeSubDimensionList = mindObj.dimensionList.reduce(
        (pre: any[], item) => {
          const list = item.subDimensionList
            .filter(sItem =>
              psyc1.psycGaugeSubDimensionIdList.includes(
                sItem.psycGaugeDimensionId
              )
            )
            .map(sItem => ({
              id: sItem.psycGaugeDimensionId,
              questionCount: sItem.questionCount
            }));

          return [...pre, ...list];
        },
        []
      );
    }

    if (psyc4.use === 1) {
      const psycId = psycGaugeIdMap[Check.BIG_FIVE_60];
      const mindObj =
        this.mindList.find(item => item.psycGaugeId === psycId) ||
        new PsycGaugeConfig();

      psyc4.psycGaugeSubDimensionList = mindObj.dimensionList
        .filter(item =>
          psyc4.psycGaugeSubDimensionIdList.includes(item.psycGaugeDimensionId)
        )
        .map(item => ({
          id: item.psycGaugeDimensionId,
          questionCount: item.questionCount
        }));
    }
  }

  // 手部检测题目渲染列表
  handOptionsList: any[] = [];

  // 考察项树选中状态
  activeType: Check = Check.DIMENSION;
  // 考察项树选中项，只有选中了DIMENSION、ENGLISH、CHINESE时使用
  activeItem?: string = "";

  // 考察项树选中事件
  async handleActive({
    activeType,
    activeItem
  }: {
    activeType: Check;
    activeItem?: string;
  }) {
    if (this.activeType === activeType && this.activeItem === activeItem)
      return;

    this.activeType = activeType;
    this.activeItem = activeItem;

    const mainBox: any = document.getElementById("freeze-scrollbar");
    mainBox.scrollTop = 0;

    // 是否选择题目
    const needSelectQuestionChecks = [
      Check.DIMENSION,
      Check.ENGLISH,
      Check.CHINESE
    ];
    // 是否心理测评
    const isPsychologicalAssessment = [
      Check.BIG_FIVE_240,
      Check.BIG_FIVE_60,
      Check.DISC,
      Check.DVF
    ];
    if (needSelectQuestionChecks.includes(activeType) && this.activeItem) {
      this.searchQuestionKeywords = "";
      await this.getQuestionList();
    }
    if (isPsychologicalAssessment.includes(activeType) && this.activeType) {
      this.getPsychologicalAssessmentQuestionList(this.activeType);
    }
    if (this.activeType === 1) {
      // 手部检测模块渲染
      this.handOptionsList = [
        {
          title: "手指完整度",
          titleDesc:
            "使用AI技术检测手指完整度，分析图像中手指的结构，评估候选人是存在残指、骨骼不完整等情况。",
          key: "complete"
        },
        {
          title: "手指灵活度",
          titleDesc:
            "使用AI技术分析手指运动范国和关节活动，评估候选人是否能够完成抓握等动作。",
          key: "flexible"
        },
        {
          title: "手部疤痕纹身",
          titleDesc:
            "通过图片分析提供准确辨识，识别候选人手部是否存在手术疤痕和纹身图案等不同特征。",
          key: "scar"
        }
      ];
    }
  }

  // 初始化考察项树选中状态
  initActiveType() {
    if (this.physicalInstrument.fingerTest.use === 1) {
      return this.handleActive({ activeType: Check.FINGER_TEST });
    }

    if (this.knowledgeAndSkills.iseEnglish.use === 1) {
      return this.handleActive({
        activeType: Check.ENGLISH,
        activeItem: "英语"
      });
    }

    if (this.knowledgeAndSkills.iseChinese.use === 1) {
      return this.handleActive({
        activeType: Check.CHINESE,
        activeItem: "普通话"
      });
    }

    if (this.basicQualityList.length > 0) {
      return this.handleActive({
        activeType: Check.DIMENSION,
        activeItem: this.basicQualityList[0].dimensionKey
      });
    }

    if (this.psychologicalMotivation.psyc1.use === 1) {
      return this.handleActive({ activeType: Check.BIG_FIVE_240 });
    }

    if (this.psychologicalMotivation.psyc4.use === 1) {
      return this.handleActive({ activeType: Check.BIG_FIVE_60 });
    }
  }

  // 题库
  isLoadingQuestions = false;
  searchQuestionKeywords = "";
  questionList: QuestionV2[] = [];
  activePsycGaugeKey = "";

  // 选中题目区域展开状态
  isExpand = true;

  // 查询题库题目
  async getQuestionList() {
    const dimensionKey = this.activeItem;
    const questionType =
      dimensionKey === "自定义客观题"
        ? undefined
        : EnumQuestionType.EnumQuestionTypeVideo;
    const params = {
      dimensionKeyFilterList: [dimensionKey],
      dimensionKey,
      questionType,
      descOrRemarkLike: this.searchQuestionKeywords || undefined,
      onlyHasOneDimension: 1,
      isExamEnv: 1, // 是否是面试创建/编辑场景，(历史面试数据兼容需求，写死1)
      withExamId: this.mode === "exam" ? (this.exam as ExamV3Custom).examId : 0,
      page: 1,
      pageSize: 500
    };

    try {
      this.isLoadingQuestions = true;
      const qo2 =
        this.mode === "template"
          ? await QuestionV2Object.tacGet(params, this.tacToken)
          : await QuestionV2Object.get(params);

      this.questionList = qo2.list;
      this.isLoadingQuestions = false;
    } catch (e) {
      console.error(e);
    }
  }

  // 初始化选中的题目
  get selectedQuestionList() {
    if (this.activeType === Check.ENGLISH) {
      return this.knowledgeAndSkills.iseEnglish.questions || [];
    }

    if (this.activeType === Check.CHINESE) {
      return this.knowledgeAndSkills.iseChinese.questions || [];
    }

    return (
      this.basicQualityList.find(item => item.dimensionKey === this.activeItem)
        ?.questions || []
    );
  }
  set selectedQuestionList(questionList: QuestionV2[]) {
    const questionIdList = questionList.map(item => item.questionId);

    if (this.activeType === Check.ENGLISH) {
      this.knowledgeAndSkills.iseEnglish.questionIdList = questionIdList;
      this.knowledgeAndSkills.iseEnglish.questionList = questionList;
      this.change();
      return;
    }

    if (this.activeType === Check.CHINESE) {
      this.knowledgeAndSkills.iseChinese.questionIdList = questionIdList;
      this.knowledgeAndSkills.iseChinese.questionList = questionList;
      this.change();
      return;
    }

    const dimension = this.basicQualityList.find(
      item => item.dimensionKey === this.activeItem
    );

    if (!dimension) return;

    dimension.questionIdList = questionIdList;
    dimension.questionList = questionList;
    this.change();
  }

  // 题目选中事件
  handleSelectQuestion(question: QuestionV2) {
    const { total, video } = this.exam.processInformation.questionCount;

    if (total >= 100) {
      return this.$message.warning("最多选择100道题目");
    }

    if (video >= 50 && question.getQuestionTypeText() === "视频题") {
      return this.$message.warning("最多选择50道视频题");
    }

    this.selectedQuestionList = [
      ...this.selectedQuestionList,
      new QuestionV2().assign(question)
    ];

    this.keepDistanceToDocumentBottom();
  }

  // 取消题目选中事件
  handleCancelQuestionSelection(question: QuestionV2) {
    this.selectedQuestionList = this.selectedQuestionList.filter(
      item => item.questionId !== question.questionId
    );
    this.exam.processInformation.objectiveQuestionScoreList = this.exam.processInformation.objectiveQuestionScoreList.filter(
      el => el.questionId !== question.questionId
    );

    this.keepDistanceToDocumentBottom();
  }

  // 题目编辑事件
  questionEditV3Key = 1;
  selectedQuestionsKey = 1;
  showView = false;
  showQuestionV3Key = 1;
  showEdit = false;
  targetQuestion = new QuestionV2();
  questionDetailEditTitle = "添加题目";

  handleEditQuestion(question?: QuestionV2) {
    if (!question) return;

    this.questionEditV3Key++;
    this.targetQuestion = question;
    this.questionDetailEditTitle = "编辑题目";

    this.$nextTick(() => {
      this.showEdit = true;
      (this.$refs.editQuestion as any)?.initData();
    });
  }

  handleEditQuestionSuccess(question?: QuestionV2) {
    if (!question) return;

    this.getQuestionList();

    // 新增题目，自动选中
    if (this.targetQuestion.questionId === 0) {
      return this.handleSelectQuestion(question);
    }

    // 编辑题目，判断是否是已选中的
    const qIndex = this.selectedQuestionList.findIndex(
      item => item.questionId === question.questionId
    );

    if (qIndex < 0) return;

    this.selectedQuestionList.splice(qIndex, 1, question);

    // 重新赋值触发setter
    this.selectedQuestionList = [...this.selectedQuestionList];
    this.selectedQuestionsKey++;
  }

  // 删除题目
  handleDelQuestion(question?: QuestionV2) {
    if (!question) return;

    this.selectedQuestionList = this.selectedQuestionList.filter(
      item => item.questionId !== question.questionId
    );
    this.exam.processInformation.objectiveQuestionScoreList = this.exam.processInformation.objectiveQuestionScoreList.filter(
      el => el.questionId !== question.questionId
    );

    this.getQuestionList();
    this.keepDistanceToDocumentBottom();
  }

  // 清空题目事件
  handleClearQuestions() {
    this.isExpand = true;
    this.selectedQuestionList = [];
    this.keepDistanceToDocumentBottom();
  }

  // 保持文档到底部的距离
  keepDistanceToDocumentBottom() {
    const mainBox = document.getElementById("freeze-scrollbar");

    if (!mainBox) return;

    const toBottom = mainBox.scrollHeight - mainBox.scrollTop;

    this.$nextTick(() => {
      mainBox.scrollTop = mainBox.scrollHeight - toBottom;
    });
  }

  // 查看题目详情事件
  handleViewQuestion(question?: QuestionV2) {
    if (!question) return;
    this.showQuestionV3Key++;
    this.showView = true;
    this.targetQuestion = question;
  }

  // 添加题目事件
  handleAddQuestions() {
    this.questionEditV3Key++;
    this.questionDetailEditTitle = "添加题目";
    this.showEdit = true;
    this.targetQuestion = new QuestionV2();
    this.$nextTick(() => () => {
      (this.$refs.editQuestion as any)?.initData();
    });
  }

  async mounted() {
    this.status();
    this.initSelectedPsyc();
    this.getPsychologicalAssessmentQuestionList(this.activeType);
    await this.initActiveType();
    this.pageLoading = false;
    this.status();
  }

  getPsychologicalAssessmentQuestionList(key: number) {
    const list = this.mindList.filter(
      it => it.psycGaugeId === psycGaugeIdMap[key]
    );

    this.chooseObj = list.length ? list[0] : new PsycGaugeConfig();
    if (key === Check.BIG_FIVE_60) {
      const sortList = [45, 47, 44, 46, 43];
      const newDimensionList = [] as MindDimension[];
      sortList.forEach(el => {
        const list = this.chooseObj.dimensionList.filter(
          it => it.psycGaugeDimensionId === el
        );
        newDimensionList.push(list[0]);
      });
      this.chooseObj.dimensionList = newDimensionList;
    }
    this.activePsycGaugeKey = "psyc" + psycGaugeIdMap[key];
  }
  bigFiveChoose(key: string, item: MindDimension) {
    const { psychologicalMotivation } = this.exam.processInformation;
    const changeObj = psychologicalMotivation[key] as PsyUse;
    const psyId = item.psycGaugeDimensionId;

    if (changeObj.psycGaugeSubDimensionIdList.includes(psyId)) {
      changeObj.psycGaugeSubDimensionList = changeObj.psycGaugeSubDimensionList.filter(
        el => el.id !== psyId
      );
    } else {
      changeObj.psycGaugeSubDimensionList = [
        ...changeObj.psycGaugeSubDimensionList,
        { id: item.psycGaugeDimensionId, questionCount: item.questionCount }
      ];
    }
    this.change();
    this.$forceUpdate();
  }
  isChoose(key: string, id: string | number) {
    return (
      this.exam.processInformation.psychologicalMotivation[
        key
      ].psycGaugeSubDimensionIdList.includes(id) || false
    );
  }

  checkData() {
    const validate = this.exam.processInformation.checkDataByQuestion(true);
    const { res, activeType, activeItem } = validate;

    if (res || !activeType) return res;

    this.$message.error("请为所有考查项选择内容");
    this.handleActive({ activeType, activeItem });
    return res;
  }

  isHandChoose(key: string) {
    return (
      this.exam.processInformation.physicalInstrument.fingerTest[key] === 1
    );
  }
  handChoose(key: string) {
    const value = this.exam.processInformation.physicalInstrument.fingerTest[
      key
    ];
    this.exam.processInformation.physicalInstrument.fingerTest[key] =
      value === 1 ? 0 : 1;
  }

  @Emit()
  change() {
    return;
  }
}
